import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CpiSide } from '../models/vendor-cpi-change';

// NK-351. What a card needs to know to bring itself up to date after someone else
// changed a rate. Everything here is what the save already had in hand, so publishing
// costs the originating card nothing extra.
export interface CpiChangeNotice {
  projectId: string;
  side: CpiSide;
  newCpi: number;

  // Whichever side the change was on. Vendor CPI hangs off a project vendor, client CPI
  // off a project segment, so exactly one of these is set.
  partnerId?: string;
  segmentId?: string;

  // How many rates the vendor has now run at, straight from the apply response. Vendor
  // side only - client CPI has no equivalent column.
  cpiSplitCount?: number | null;
}

// NK-351. Tells every card on a project that a CPI has changed.
//
// The cards that show a CPI sit side by side and each works out its own totals: the
// Field Plan card, the Finance card and Links & Vendors all cache the rates a split
// vendor has run at and derive costs from them. The rate itself lives on a shared
// object so it updates on its own, but the derived figures do not, so a change applied
// from one card left the others showing a stale total and a badge counting rates that
// no longer existed.
//
// This is deliberately a notification and not a store. The cards already know how to
// rebuild themselves from a change - that is what their own save handler does - so the
// only thing missing was hearing about changes they did not make. Publishers call
// `notify` after the write lands; subscribers treat their own notice the same as
// anyone else's, which keeps every card on one code path and stops the originating
// card being a special case.
@Injectable({
  providedIn: 'root'
})
export class CpiChangeService {

  private readonly changed = new Subject<CpiChangeNotice>();

  // Never completed: this is application-wide and outlives any one project page.
  // Subscribers unsubscribe on their own destroy.
  readonly changes: Observable<CpiChangeNotice> = this.changed.asObservable();

  notify(notice: CpiChangeNotice): void {
    this.changed.next(notice);
  }
}
